import { supabase } from './supabase';

/**
 * Enhanced User ID Generator
 * Generates unique user IDs based on school ID + incremental number
 * Format: {school_id}{number} (e.g., "ic1", "ic2", "ic3")
 */
export class UserIdGenerator {
  /**
   * Generate a unique user ID for a given school using database function
   * @param {string} schoolId - The school's school_id (not the UUID)
   * @returns {Promise<string|null>} - The generated user ID or null if error
   */
  static async generateUserId(schoolId) {
    try {
      // Validate school ID
      if (!schoolId || typeof schoolId !== 'string') return null;

      // Clean the school ID (remove spaces, convert to lowercase)
      const cleanSchoolId = schoolId.trim().toLowerCase();
      
      // Use the database function for server-side generation
      const { data, error } = await supabase
        .rpc('generate_user_id', { school_code: cleanSchoolId });

      if (error) return await this.generateUserIdClientSide(cleanSchoolId);
      return data;
    } catch (error) {
      try {
        return await this.generateUserIdClientSide(schoolId);
      } catch {
        return null;
      }
    }
  }

  /**
   * Client-side fallback user ID generation
   * @param {string} schoolId - The school's school_id
   * @returns {Promise<string|null>} - The generated user ID or null if error
   */
  static async generateUserIdClientSide(schoolId) {
    try {
      // Get all existing user IDs that start with this school ID
      const { data: existingUsers, error: fetchError } = await supabase
        .from('users')
        .select('user_id')
        .like('user_id', `${schoolId}%`)
        .order('user_id', { ascending: false });

      if (fetchError) return null;

      // Find the highest number for this school
      let nextNumber = 1;
      
      if (existingUsers && existingUsers.length > 0) {
        // Extract numbers from existing user IDs
        const numbers = existingUsers
          .map(user => {
            const match = user.user_id.match(new RegExp(`^${schoolId}(\\d+)$`));
            return match ? parseInt(match[1]) : 0;
          })
          .filter(num => num > 0);
        
        // Get the highest number and add 1
        if (numbers.length > 0) {
          nextNumber = Math.max(...numbers) + 1;
        }
      }

      // Generate the new user ID
      const newUserId = `${schoolId}${nextNumber}`;
      
      // Double-check uniqueness (extra safety measure)
      const { data: duplicateCheck, error: duplicateError } = await supabase
        .from('users')
        .select('user_id')
        .eq('user_id', newUserId)
        .limit(1);

      if (duplicateError) return null;
      if (duplicateCheck && duplicateCheck.length > 0) {
        return await this.generateUserIdClientSide(schoolId);
      }
      return newUserId;
    } catch (error) {
      return null;
    }
  }

  /**
   * Validate if a user ID follows the correct format
   * @param {string} userId - The user ID to validate
   * @returns {boolean} - True if valid format
   */
  static validateUserIdFormat(userId) {
    if (!userId || typeof userId !== 'string') return false;
    
    // Pattern: school_id (letters) + number (digits)
    const pattern = /^[a-zA-Z]+[0-9]+$/;
    return pattern.test(userId);
  }

  /**
   * Extract school ID from user ID
   * @param {string} userId - The user ID
   * @returns {string|null} - The school ID part or null if invalid
   */
  static extractSchoolId(userId) {
    if (!this.validateUserIdFormat(userId)) return null;
    
    // Extract the letter part (school ID)
    const match = userId.match(/^([a-zA-Z]+)/);
    return match ? match[1].toLowerCase() : null;
  }

  /**
   * Extract user number from user ID
   * @param {string} userId - The user ID
   * @returns {number|null} - The number part or null if invalid
   */
  static extractUserNumber(userId) {
    if (!this.validateUserIdFormat(userId)) return null;
    
    // Extract the number part
    const match = userId.match(/([0-9]+)$/);
    return match ? parseInt(match[1]) : null;
  }

  /**
   * Get the next available user number for a school
   * @param {string} schoolId - The school ID
   * @returns {Promise<number>} - The next available number
   */
  static async getNextUserNumber(schoolId) {
    try {
      const { data: existingUsers, error } = await supabase
        .from('users')
        .select('user_id')
        .like('user_id', `${schoolId.toLowerCase()}%`)
        .order('user_id', { ascending: false });

      if (error) return 1;

      if (!existingUsers || existingUsers.length === 0) {
        return 1;
      }

      const numbers = existingUsers
        .map(user => {
          const match = user.user_id.match(new RegExp(`^${schoolId.toLowerCase()}(\\d+)$`));
          return match ? parseInt(match[1]) : 0;
        })
        .filter(num => num > 0);

      return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
    } catch (error) {
      return 1;
    }
  }

  /**
   * Get all users for a specific school
   * @param {string} schoolId - The school's school_id
   * @returns {Promise<Array>} - Array of users for the school
   */
  static async getUsersBySchool(schoolId) {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .like('user_id', `${schoolId.toLowerCase()}%`)
        .order('user_id', { ascending: true });

      if (error) return [];

      return users || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get statistics for a school's user IDs
   * @param {string} schoolId - The school's school_id
   * @returns {Promise<Object>} - Statistics object
   */
  static async getSchoolUserStats(schoolId) {
    try {
      const users = await this.getUsersBySchool(schoolId);
      
      const stats = {
        totalUsers: users.length,
        highestNumber: 0,
        lowestNumber: 0,
        missingNumbers: [],
        userIds: users.map(u => u.user_id).sort()
      };

      if (users.length > 0) {
        const numbers = users.map(user => this.extractUserNumber(user.user_id)).filter(n => n !== null);
        stats.highestNumber = Math.max(...numbers);
        stats.lowestNumber = Math.min(...numbers);
        
        // Find missing numbers in the sequence
        for (let i = 1; i <= stats.highestNumber; i++) {
          if (!numbers.includes(i)) {
            stats.missingNumbers.push(i);
          }
        }
      }

      return stats;
    } catch (error) {
      return {
        totalUsers: 0,
        highestNumber: 0,
        lowestNumber: 0,
        missingNumbers: [],
        userIds: []
      };
    }
  }
}

export default UserIdGenerator;